import { setCaseDocumentsList, setCaseDocumentsRowCount } from '@/store';
import { api } from '../core';
import { CASE_DOCUMENTS_TAG } from '../constants';

export const CaseDocumentStatus = {
  UPLOADING: 'UPLOADING',
  UPLOADED: 'UPLOADED',
  UPLOAD_FAILED: 'UPLOAD_FAILED',
  VALIDATION_ERROR: 'VALIDATION_ERROR',
  READY_FOR_AI: 'READY_FOR_AI',
  REMOVING_BREACH_PAGES: 'REMOVING_BREACH_PAGES',
  AI_PROCESSING_COMPLETE: 'AI_PROCESSING_COMPLETE',
} as const;

export type CaseDocumentStatus = (typeof CaseDocumentStatus)[keyof typeof CaseDocumentStatus] | (string & {});

export interface CaseProviderData {
  providerId: string;
  providerName: string;
  providerType: string;
}

export interface CaseUploadedBy {
  userId: string;
  userName: string;
}

export interface CaseCheckedBy {
  userId: string;
  userName: string;
}

export interface CaseDocument {
  canDownload: boolean;
  checkCompleted: string | null;
  checkStarted: string | null;
  checkedBy: CaseCheckedBy | null;
  documentId: string;
  documentName: string;
  documentProcessingFailureReason: string | null;
  pageCount: number | null;
  providerData: CaseProviderData;
  sizeMb: number;
  status: CaseDocumentStatus;
  uploadedAt: string | null;
  uploadedBy: CaseUploadedBy;
}

export interface CaseDocumentsResponse {
  documents: CaseDocument[];
  numberOfResults: number;
}

type CaseDocumentsProps = {
  caseId: string;
  pageIndex: number;
  pageSize: number;
};

const caseDocumentsApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getCaseDocuments: builder.query<CaseDocumentsResponse, CaseDocumentsProps>({
      query: ({ caseId, pageIndex, pageSize }: CaseDocumentsProps) => ({
        url: `case/documents?case_id=${caseId}&page_index=${pageIndex}&page_size=${pageSize}`,
        method: 'GET',
      }),
      providesTags: (_result, _err, { caseId, pageIndex, pageSize }) => [
        {
          type: CASE_DOCUMENTS_TAG,
          id: caseId,
          pageIndex: pageIndex,
          pageSize: pageSize,
        },
      ],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;

          if (data) {
            dispatch(setCaseDocumentsList(data.documents));
            dispatch(setCaseDocumentsRowCount(data.numberOfResults));
          }
        } catch (error) {
          if (error instanceof Error && error.name === 'AbortError') {
            console.log('Request was aborted');
          } else {
            console.error('Error in getCaseDocuments in onQueryStarted:', error);
          }
        }
      },
      extraOptions: { maxRetries: 3 },
    }),
  }),
});

/**
 * Export hooks for usage in functional components, which are
 * auto-generated based on the defined endpoints
 */
export const { useLazyGetCaseDocumentsQuery } = caseDocumentsApi;

/**
 * Export endpoints for usage in other queries or mutations
 */
export const { getCaseDocuments } = caseDocumentsApi.endpoints;
